import { MAX_FOOTPRINT_SHAPES_PER_LEVEL } from '@alpinehomeair/common';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { FootprintShapeType, LevelType, PositionType } from 'data/types';
import {
  beginUndoBatch,
  endUndoBatch,
  setLevelFootprintWithMirror
} from 'features/structure';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { getNeighborFootprints } from 'utils/footprint-adjacent';
import { NeighborFootprint } from 'utils/footprint-adjacent-types';
import {
  extendFootprintWithStroke,
  rotateFootprintShapes
} from 'utils/footprint-extend';
import { FootprintRotationDirection } from 'utils/footprint-extend-types';
import {
  FootprintMode,
  resolveFootprintModeAfterShapeCountChange
} from 'utils/footprint-mode';
import { getEffectiveFootprintPolygons } from 'utils/footprint-polygons';
import { payloadFromEditedShapes } from 'utils/footprint-shapes-commit';
import {
  FOOTPRINT_HOLE_MESSAGE,
  FOOTPRINT_HOLE_TOAST_ID,
  FOOTPRINT_SHAPE_LIMIT_MESSAGE
} from 'utils/footprint-shape-messages';
import {
  cloneFootprintShapes,
  footprintShapesEncloseHole,
  footprintShapesToPoints,
  getEffectiveFootprintShapes
} from 'utils/footprint-union';
import { UseFootprintEditingResult } from './use-footprint-editing-types';

export function useFootprintEditing(
  activeLevel: LevelType | undefined
): UseFootprintEditingResult {
  const dispatch = useAppDispatch();
  const { structure, footprint } = useAppSelector((state) => state.structure);
  const [footprintMode, setFootprintMode] = useState<FootprintMode>('draw');
  const [drawingResetKey, setDrawingResetKey] = useState(0);
  const layoutImageDimsRef = useRef<{
    naturalWidth: number;
    naturalHeight: number;
  } | null>(null);

  const effectiveFootprintShapes = useMemo(
    () => getEffectiveFootprintShapes(activeLevel),
    [activeLevel]
  );
  const effectiveFootprintPolygons = useMemo(
    () => getEffectiveFootprintPolygons(activeLevel),
    [activeLevel]
  );
  const canAddFootprintShape =
    effectiveFootprintShapes.length < MAX_FOOTPRINT_SHAPES_PER_LEVEL;
  const previousFootprintLevelIdRef = useRef(activeLevel?.uniqueId);
  const previousFootprintShapeCountRef = useRef(
    effectiveFootprintShapes.length
  );

  useEffect(() => {
    const nextCount = effectiveFootprintShapes.length;
    const sameLevel =
      previousFootprintLevelIdRef.current === activeLevel?.uniqueId;
    if (sameLevel) {
      setFootprintMode((mode) =>
        resolveFootprintModeAfterShapeCountChange(
          previousFootprintShapeCountRef.current,
          nextCount,
          mode
        )
      );
    }
    previousFootprintLevelIdRef.current = activeLevel?.uniqueId;
    previousFootprintShapeCountRef.current = nextCount;
  }, [activeLevel?.uniqueId, effectiveFootprintShapes.length]);

  const commitFootprintShapes = useCallback(
    (shapes: FootprintShapeType[]) => {
      if (!activeLevel) return;
      if (footprintShapesEncloseHole(footprintShapesToPoints(shapes))) {
        toast.error(FOOTPRINT_HOLE_MESSAGE, { id: FOOTPRINT_HOLE_TOAST_ID });
        return;
      }
      dispatch(
        setLevelFootprintWithMirror({
          levelUniqueId: activeLevel.uniqueId,
          ...payloadFromEditedShapes(shapes)
        })
      );
    },
    [dispatch, activeLevel]
  );

  const handleFootprintShapeChange = useCallback(
    (index: number, points: PositionType[], vertexEdited: boolean) => {
      const next = effectiveFootprintShapes.map((shape, shapeIndex) =>
        shapeIndex === index ? { points, vertexEdited } : shape
      );
      commitFootprintShapes(next);
    },
    [commitFootprintShapes, effectiveFootprintShapes]
  );

  const handleDeleteFootprintShape = useCallback(
    (index: number) => {
      commitFootprintShapes(
        effectiveFootprintShapes.filter((_, shapeIndex) => shapeIndex !== index)
      );
    },
    [commitFootprintShapes, effectiveFootprintShapes]
  );

  const clearAllShapes = useCallback(() => {
    commitFootprintShapes([]);
    setFootprintMode((mode) =>
      resolveFootprintModeAfterShapeCountChange(
        previousFootprintShapeCountRef.current,
        0,
        mode
      )
    );
  }, [commitFootprintShapes]);

  const handleAddFootprintShape = useCallback(
    (shape: PositionType[]) => {
      if (!canAddFootprintShape) {
        toast.error(FOOTPRINT_SHAPE_LIMIT_MESSAGE);
        return;
      }
      commitFootprintShapes([
        ...effectiveFootprintShapes,
        { points: shape, vertexEdited: true }
      ]);
      setFootprintMode('edit');
    },
    [canAddFootprintShape, commitFootprintShapes, effectiveFootprintShapes]
  );

  const handleFootprintStrokeComplete = useCallback(
    (rawPoints: PositionType[]) => {
      const hadShapes = effectiveFootprintShapes.length > 0;
      const result = extendFootprintWithStroke(
        effectiveFootprintShapes,
        rawPoints
      );
      if (result.status === 'invalid-stroke') {
        toast.error("That stroke didn't make a shape");
        return;
      }
      if (result.status === 'limit-reached') {
        toast.error(FOOTPRINT_SHAPE_LIMIT_MESSAGE);
        return;
      }
      if (result.status !== 'applied') return;
      commitFootprintShapes(result.shapes);
      if (!hadShapes) {
        setFootprintMode('edit');
      }
    },
    [commitFootprintShapes, effectiveFootprintShapes]
  );

  const handleRotateFootprint = useCallback(
    (direction: FootprintRotationDirection) => {
      if (effectiveFootprintShapes.length === 0) return;
      commitFootprintShapes(
        rotateFootprintShapes(effectiveFootprintShapes, direction)
      );
    },
    [commitFootprintShapes, effectiveFootprintShapes]
  );

  const handleFootprintDragStart = useCallback(() => {
    dispatch(beginUndoBatch());
  }, [dispatch]);

  const handleFootprintDragEnd = useCallback(() => {
    dispatch(endUndoBatch());
  }, [dispatch]);

  const { below: levelBelowFootprint, above: levelAboveFootprint } = useMemo(
    () => getNeighborFootprints(structure, activeLevel),
    [activeLevel, structure, footprint]
  );

  const copyShapes = useCallback(
    (neighbor: NeighborFootprint | null) => {
      if (!neighbor || neighbor.shapes.length === 0 || !activeLevel) return;
      commitFootprintShapes(cloneFootprintShapes(neighbor.shapes));
      setDrawingResetKey((k) => k + 1);
    },
    [activeLevel, commitFootprintShapes]
  );

  const handleCopyLevelBelow = useCallback(() => {
    copyShapes(levelBelowFootprint);
  }, [copyShapes, levelBelowFootprint]);

  const handleCopyLevelAbove = useCallback(() => {
    copyShapes(levelAboveFootprint);
  }, [copyShapes, levelAboveFootprint]);

  return {
    footprintMode,
    setFootprintMode,
    drawingResetKey,
    layoutImageDimsRef,
    effectiveFootprintPolygons,
    effectiveFootprintShapes,
    canAddFootprintShape,
    handleFootprintShapeChange,
    handleDeleteFootprintShape,
    handleAddFootprintShape,
    handleFootprintStrokeComplete,
    handleRotateFootprint,
    handleFootprintDragStart,
    handleFootprintDragEnd,
    handleResetFootprint: clearAllShapes,
    handleCopyLevelBelow,
    handleCopyLevelAbove,
    levelBelowFootprint,
    levelAboveFootprint
  };
}
